import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Chip } from '@material-ui/core'
import { useIntl } from 'react-intl'
import * as R from 'ramda'
import { makeStyles } from '@material-ui/styles'
import FetchTable from 'app/components/table/FetchTable'
import clsx from 'clsx'
import DeleteDialogTopicCard from './DeleteDialogTopicCard'
import { withTopicsDeleteColumn } from 'app/components/table/TableColumn'
import NotificationEmptyCard from './NotificationEmptyCard'
import { FilterList } from '@material-ui/icons'
import './styles/notification-topic-table.css'
import { setDeleteTopicDialogState } from './store/actions/topic-action'
import { TOPIC_CARD_STATUS } from './configs/constants'
import effects from 'app/effects/effects'
import TopicSelectFilter from './TopicSelectFilter'
import MessageAlert from 'app/components/MessageAlert'

const useStyles = makeStyles((theme) => ({
  addButton: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    zIndex: 99,
    backgroundColor: theme.palette.secondary.main,
  },
  topicTable: {
    '& .rt-thead': {
      '@media(max-width:700px)': {
        width: '900px',
      },
      '@media(max-width:412px)': {
        width: '1100px',
      },
    },
    '& .rt-tbody': {
      '@media(max-width:700px)': {
        width: '900px',
      },
      '@media(max-width:412px)': {
        width: '1100px',
      },
    },
  },
  textActive: {
    backgroundColor: theme.palette.active.main,
    color: '#ffffff',
  },
  textInactive: {
    backgroundColor: '#f74872',
    color: '#ffffff',
  },
  headerPadding: {
    padding: '17px 5px 17px 18px',
  },
  itemPadding: {
    padding: '0 0 0 18px',
  },
}))

const NotificationTopicTable = ({
  onClickRow,
  pageInfo,
  onFetchData,
  refetchData,
  endFetchData,
  startFetchData,
  showPagination,
  defaultPageSize,
  isNoTopicCard,
  filter,
  onChangeFilterByKey,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()

  const { deleteDialogState } = useSelector((state) => state.topicReducer)

  const mapTableRowdata = (row) => {
    return { ...row, isEditMode: true }
  }

  const handleOnClick = (data) => {
    onClickRow(mapTableRowdata(data))
  }

  if (isNoTopicCard) {
    return <NotificationEmptyCard />
  }

  const topicStatus = (formatMessage) => (status) => {
    switch (status) {
      case TOPIC_CARD_STATUS.ACTIVE:
        return formatMessage({ id: 'status.active' })
      case TOPIC_CARD_STATUS.INACTIVE:
        return formatMessage({ id: 'status.inactive' })
      default:
        return null
    }
  }

  const topicStatusConfig = (formatMessage) =>
    R.o(topicStatus(formatMessage), R.prop('isActive'))

  const setColorStatus = (status, classes) => {
    switch (status) {
      case TOPIC_CARD_STATUS.ACTIVE:
        return classes.textActive
      case TOPIC_CARD_STATUS.INACTIVE:
        return classes.textInactive
      default:
        return null
    }
  }

  const handleDeleteRow = (id) => {
    if (R.prop('isOpenDeleteDialog', deleteDialogState)) {
      effects.apis
        .deleteTopic({ id })
        .then((res) => {
          return onDeleteSuccessPromise()
        })
        .catch((error) => {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'error.general' }),
            }),
          )
        })
    }
    if (R.prop('isOpenDeactivateDialog', deleteDialogState)) {
      effects.apis
        .updateTopic({ ...deleteDialogState, isActive: 0 })
        .then((res) => {
          return onDeleteSuccessPromise()
        })
        .catch((error) => {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'error.general' }),
            }),
          )
        })
    }
  }

  const onDeleteSuccessPromise = () => {
    startFetchData()
    return refetchData().then(endFetchData)
  }

  const refetchStatus = () => {
    refetchData()
  }

  const handleFollower = (props) => {
    const isActive = R.path(['original', 'isActive'], props)
    const subscribersCount = R.path(['original', 'subscribersCount'], props)
    const inactiveFollower = R.path(['original', 'inactiveFollower'], props)

    return isActive !== 0 ? subscribersCount : inactiveFollower
  }

  return (
    <>
      <div className="topics-table-filters">
        <FilterList />
        <TopicSelectFilter
          refetch={refetchStatus}
          filter={filter}
          onChangeFilterByKey={onChangeFilterByKey}
        />
      </div>
      <div className="h-full overflow-hidden flex flex-col flex-auto">
        <FetchTable
          className={classes.topicTable}
          FilterComponent={null}
          page={pageInfo.page}
          pages={pageInfo.total}
          data={pageInfo.data}
          total={pageInfo.totalData}
          error={pageInfo.error}
          refetchData={refetchData}
          defaultPageSize={defaultPageSize}
          manual
          loading={pageInfo.isLoading}
          onFetchData={onFetchData}
          onClickRow={handleOnClick}
          columns={[
            {
              Header: (props) => (
                <div className={classes.headerPadding}>
                  {formatMessage({
                    id: 'notifications.topics.table.name',
                  })}
                </div>
              ),
              accessor: 'name',
              type: 'text',
              Cell: (props) => {
                // return (<p>{props.value}</p>)
                return (
                  <div className={classes.itemPadding}>
                    <p>{props.original.name}</p>
                  </div>
                )
              },
            },
            {
              Header: formatMessage({
                id: 'notifications.topics.table.column.description',
              }),
              accessor: 'description',
              filterable: false,
              type: 'text',
              sortable: false,
            },

            {
              Header: formatMessage({
                id: 'notifications.topics.table.followers',
              }),
              accessor: 'followers',
              filterable: false,
              sortable: false,
              type: 'text',
              Cell: (props) => {
                return handleFollower(props)
              },
            },
            {
              Header: formatMessage({
                id: 'notifications.topics.table.messages',
              }),
              accessor: 'messages',
              filterable: false,
              sortable: false,
              type: 'text',
              Cell: (props) => {
                return R.path(['original', 'notificationsCount'], props)
              },
            },
            {
              id: 'isActive',
              Header: formatMessage({
                id: 'text.status',
              }),
              accessor: 'isActive',
              filterable: false,
              type: 'text',
              Cell: (props) => (
                <div className="w-full">
                  <Chip
                    label={topicStatusConfig(formatMessage)(props.original)} // topicStatusConfig(formatMessage)(props.original) instead props.value
                    className={clsx(
                      'h-10',
                      setColorStatus(props.original.isActive || 0, classes),
                    )}
                    size="medium"
                  />
                </div>
              ),
            },
            withTopicsDeleteColumn(
              ({ original }) => {
                if (Number(original.notificationsCount) !== 0) {
                  dispatch(
                    setDeleteTopicDialogState({
                      isOpen: true,
                      isOpenDeactivateDialog: true,
                      ...original,
                    }),
                  )
                } else
                  dispatch(
                    setDeleteTopicDialogState({
                      isOpen: true,
                      isOpenDeleteDialog: true,
                      ...original,
                    }),
                  )
              },
              true,
              (cell) =>
                !cell.original.isActive && cell.original.notificationsCount,
              false,
              80,
            ),
          ]}
          showPagination={showPagination}
        />
        <DeleteDialogTopicCard
          classes={classes}
          isOpen={R.prop('isOpen', deleteDialogState)}
          onClose={() => {
            dispatch(
              setDeleteTopicDialogState({
                ...deleteDialogState,
                isOpen: false,
              }),
            )
          }}
          onSubmit={handleDeleteRow}
          cardName={deleteDialogState?.name}
          topicId={deleteDialogState?.id}
        />
      </div>
    </>
  )
}

export default NotificationTopicTable
